import { useEffect, useRef } from 'react';

// Firefox reports mouse wheels in lines; Chrome and Safari report pixels.
const WHEEL_LINE_HEIGHT_PX = 16;
// Sub-pixel scroll offsets must not keep the wheel captured at either end.
const EDGE_EPSILON_PX = 1;
// Absorbs float drift when many tiny deltas add up to a whole pixel (10 x 0.3 = 2.9999...).
const ACCUMULATION_EPSILON_PX = 1e-6;

function verticalWheelDeltaPx(event: WheelEvent, element: HTMLElement): number {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        return event.deltaY * WHEEL_LINE_HEIGHT_PX;
    }
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        return event.deltaY * element.clientWidth;
    }
    return event.deltaY;
}

function canScrollHorizontally(element: HTMLElement, deltaPx: number): boolean {
    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    if (maxScrollLeft <= EDGE_EPSILON_PX || deltaPx === 0) {
        return false;
    }
    return deltaPx < 0
        ? element.scrollLeft > EDGE_EPSILON_PX
        : element.scrollLeft < maxScrollLeft - EDGE_EPSILON_PX;
}

// A leftover fraction only carries over while the wheel keeps its direction.
function carriedRemainderPx(remainderPx: number, deltaPx: number): number {
    return Math.sign(remainderPx) === Math.sign(deltaPx) ? remainderPx : 0;
}

// A vertical wheel scrolls the element sideways while it can still move that way;
// at either end the wheel passes through to the page. Pass `null` to opt out.
export function useWheelToHorizontalScroll(element: HTMLElement | null): void {
    const remainderPxRef = useRef(0);

    useEffect(() => {
        if (!element) {
            return undefined;
        }
        remainderPxRef.current = 0;
        const onWheel = (event: WheelEvent) => {
            // Horizontal trackpad gestures scroll natively; ctrl+wheel is pinch-zoom.
            if (event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
                return;
            }
            const deltaPx = verticalWheelDeltaPx(event, element);
            const pendingPx = carriedRemainderPx(remainderPxRef.current, deltaPx) + deltaPx;
            if (!canScrollHorizontally(element, pendingPx)) {
                remainderPxRef.current = 0;
                return;
            }
            event.preventDefault();
            // Whole pixels only, so a very slow swipe accumulates instead of rounding away.
            const wholePx = Math.trunc(pendingPx + Math.sign(pendingPx) * ACCUMULATION_EPSILON_PX);
            remainderPxRef.current = pendingPx - wholePx;
            if (wholePx !== 0) {
                element.scrollLeft += wholePx;
            }
        };
        // Non-passive so preventDefault can stop the page scrolling; React's onWheel is passive.
        element.addEventListener('wheel', onWheel, { passive: false });
        return () => element.removeEventListener('wheel', onWheel);
    }, [element]);
}
